import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAITrainingParams } from '../apiClient';

const STORAGE_KEY = "buck_euchre_ai_learning";

function getRate(attempts, successes) {
  if (!attempts || attempts === 0) return null;
  return ((successes / attempts) * 100).toFixed(1);
}

function StatBar({ label, attempts, successes }) {
  const rate = getRate(attempts, successes);
  if (rate === null) return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: "#4b5563" }}>No data</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4 }} />
    </div>
  );

  const rateNum = parseFloat(rate);
  const barColor = rateNum >= 60 ? "#22c55e" : rateNum >= 40 ? "#f59e0b" : "#ef4444";

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#c9d1d9", marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ color: barColor, fontWeight: 600 }}>{rate}% ({successes}/{attempts})</span>
      </div>
      <div style={{ height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: `${Math.min(rateNum, 100)}%`,
          background: barColor,
          borderRadius: 4,
          transition: "width 0.3s"
        }} />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: "#161b22",
      border: "1px solid #30363d",
      borderRadius: 10,
      padding: "18px 22px",
      marginBottom: 20
    }}>
      <h3 style={{ color: "#58a6ff", fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{title}</h3>
      {children}
    </div>
  );
}

export default function AIStatsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [serverData, setServerData] = useState(null);
  const [dataSource, setDataSource] = useState(null); // 'server' or 'local'
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    // Try server first, fall back to localStorage
    getAITrainingParams().then(params => {
      if (params && params.totalGamesAnalyzed > 0) {
        setServerData(params);
        setDataSource('server');
      } else {
        // Fall back to localStorage
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) { setData(JSON.parse(stored)); setDataSource('local'); }
        } catch { /* no data */ }
      }
    }).catch(() => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) { setData(JSON.parse(stored)); setDataSource('local'); }
      } catch { /* no data */ }
    });
  }, []);

  if (!user?.isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e2e8f0', fontFamily: "'Georgia', serif" }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, marginBottom: 12 }}>Access Denied</h1>
          <p style={{ color: '#8b949e' }}>You do not have admin privileges.</p>
          <button onClick={() => navigate('/home')} style={{ marginTop: 16, background: '#238636', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 20px', fontSize: 14, cursor: 'pointer' }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Render server-aggregated data
  if (dataSource === 'server' && serverData) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', padding: '40px 24px', fontFamily: "'Georgia', serif", color: '#e2e8f0' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0' }}>AI Learning Stats</h1>
            <button onClick={() => navigate('/home')} style={{ background: 'transparent', border: '1px solid #30363d', color: '#58a6ff', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>
              Back to Home
            </button>
          </div>

          <div style={{ display: 'inline-block', background: '#238636', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 10, marginBottom: 16 }}>
            Server-aggregated
          </div>

          <Section title="Overview">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#58a6ff" }}>{serverData.totalGamesAnalyzed}</div>
                <div style={{ fontSize: 11, color: "#8b949e" }}>Games Analyzed</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#a78bfa" }}>{(serverData.aggressivenessMultiplier * 100).toFixed(0)}%</div>
                <div style={{ fontSize: 11, color: "#8b949e" }}>Aggression Level</div>
              </div>
              <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#fbbf24" }}>{serverData.bestTrumpCallThreshold}</div>
                <div style={{ fontSize: 11, color: "#8b949e" }}>Trump Call Threshold</div>
              </div>
            </div>
            <div style={{ fontSize: 10, color: "#4b5563", marginTop: 10, textAlign: "right" }}>
              Computed at: {new Date(serverData.computedAt).toLocaleString()}
            </div>
          </Section>

          <Section title="Trump Calling by Count">
            {serverData.trumpCallingByCount?.map(row => (
              <StatBar key={row.trumpCount} label={`${row.trumpCount} trump in hand`} attempts={row.attempts} successes={row.successes} />
            ))}
          </Section>

          <Section title="Play Decision Stats">
            {serverData.playDecisionStats?.map((row, i) => (
              <div key={i} style={{ fontSize: 12, color: '#c9d1d9', marginBottom: 6 }}>
                <span style={{ color: row.decision === 'play' ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>{row.decision}</span> with {row.trumpCount} trump:
                {' '}{row.attempts} times, avg {row.avgTricks} tricks, {row.madeTricks}/{row.attempts} made a trick
              </div>
            ))}
          </Section>

          <Section title="Current AI Thresholds">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Trump Call Threshold</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#fbbf24" }}>{serverData.bestTrumpCallThreshold} cards</div>
              </div>
              <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Play Threshold</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#22c55e" }}>{serverData.bestPlayThreshold} trump</div>
              </div>
              <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Euchre Rate</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#ef4444" }}>{serverData.euchreRate}%</div>
              </div>
              <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Aggression</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#a78bfa" }}>
                  {serverData.aggressivenessMultiplier.toFixed(2)}x
                  <span style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
                    {serverData.aggressivenessMultiplier > 1.1 ? "Aggressive" : serverData.aggressivenessMultiplier < 0.9 ? "Conservative" : "Balanced"}
                  </span>
                </div>
              </div>
            </div>
          </Section>

          <button
            onClick={() => setShowRaw(!showRaw)}
            style={{ background: "#161b22", border: "1px solid #30363d", color: "#8b949e", borderRadius: 6, padding: "8px 16px", fontSize: 12, cursor: "pointer", marginBottom: 12 }}
          >
            {showRaw ? "Hide" : "Show"} Raw Data
          </button>

          {showRaw && (
            <pre style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 8, padding: 16, fontSize: 11, color: "#8b949e", overflow: "auto", maxHeight: 400 }}>
              {JSON.stringify(serverData, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  }

  // Fall back to localStorage data (original display)
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1117',
      padding: '40px 24px',
      fontFamily: "'Georgia', serif",
      color: '#e2e8f0'
    }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e2e8f0' }}>AI Learning Stats</h1>
          <button
            onClick={() => navigate('/home')}
            style={{
              background: 'transparent',
              border: '1px solid #30363d',
              color: '#58a6ff',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 13,
              cursor: 'pointer'
            }}
          >
            Back to Home
          </button>
        </div>

        {dataSource === 'local' && (
          <div style={{ display: 'inline-block', background: '#f59e0b', color: '#000', borderRadius: 4, padding: '2px 8px', fontSize: 10, marginBottom: 16 }}>
            Local only
          </div>
        )}

        {!data || data.gamesAnalyzed === 0 ? (
          <div style={{
            background: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 10,
            padding: 40,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#9824;</div>
            <h2 style={{ color: '#c9d1d9', marginBottom: 8 }}>No Data Yet</h2>
            <p style={{ color: '#8b949e', fontSize: 14 }}>
              Play some games and the AI will start learning! Stats will appear here after your first completed game.
            </p>
          </div>
        ) : (
          <>
            {/* Overview */}
            <Section title="Overview">
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#58a6ff" }}>{data.gamesAnalyzed}</div>
                  <div style={{ fontSize: 11, color: "#8b949e" }}>Games Analyzed</div>
                </div>
                <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#a78bfa" }}>{(data.patterns.aggressivenessMultiplier * 100).toFixed(0)}%</div>
                  <div style={{ fontSize: 11, color: "#8b949e" }}>Aggression Level</div>
                </div>
                <div style={{ flex: 1, minWidth: 120, background: "#0d1117", borderRadius: 8, padding: "12px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#fbbf24" }}>{data.patterns.bestTrumpCallThreshold}</div>
                  <div style={{ fontSize: 11, color: "#8b949e" }}>Trump Call Threshold</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: "#4b5563", marginTop: 10, textAlign: "right" }}>
                Last updated: {new Date(data.lastUpdated).toLocaleString()}
              </div>
            </Section>

            {/* Trump Calling */}
            <Section title="Trump Calling Success Rates">
              <StatBar label="With Right Bower" attempts={data.trumpCalling.withRightBower.attempts} successes={data.trumpCalling.withRightBower.successes} />
              <StatBar label="With Left Bower" attempts={data.trumpCalling.withLeftBower.attempts} successes={data.trumpCalling.withLeftBower.successes} />
              <StatBar label="With 3+ Trump" attempts={data.trumpCalling.with3Trump.attempts} successes={data.trumpCalling.with3Trump.successes} />
              <StatBar label="With 2 Trump" attempts={data.trumpCalling.with2Trump.attempts} successes={data.trumpCalling.with2Trump.successes} />
              <StatBar label="Dealer Pickup" attempts={data.trumpCalling.dealerPickup.attempts} successes={data.trumpCalling.dealerPickup.successes} />
            </Section>

            {/* Play/Sit Decisions */}
            <Section title="Play/Sit Decisions">
              <StatBar label="Play with 1 Trump" attempts={data.playDecisions.playWith1Trump.attempts} successes={data.playDecisions.playWith1Trump.successes} />
              <StatBar label="Play with 2+ Trump" attempts={data.playDecisions.playWith2Trump.attempts} successes={data.playDecisions.playWith2Trump.successes} />
              {data.playDecisions.sitOut.attempts > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#c9d1d9", marginBottom: 4 }}>
                    <span>Sit Out</span>
                    <span style={{ color: "#8b949e" }}>
                      {data.playDecisions.sitOut.attempts} times ({data.playDecisions.sitOut.losses} losses avoided)
                    </span>
                  </div>
                </div>
              )}
            </Section>

            {/* Card Play Patterns */}
            <Section title="Card Play Patterns">
              <StatBar label="Lead High Trump" attempts={data.cardPlay.leadHighTrump.attempts} successes={data.cardPlay.leadHighTrump.tricksWon} />
              <StatBar label="Lead Off-Ace" attempts={data.cardPlay.leadOffAce.attempts} successes={data.cardPlay.leadOffAce.tricksWon} />
              <StatBar label="Economical Wins" attempts={data.cardPlay.economicalWins.attempts} successes={data.cardPlay.economicalWins.successes} />
            </Section>

            {/* Current Thresholds */}
            <Section title="Current AI Thresholds">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Trump Call Threshold</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#fbbf24" }}>{data.patterns.bestTrumpCallThreshold} cards</div>
                </div>
                <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Play Threshold</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#22c55e" }}>{data.patterns.bestPlayThreshold} trump</div>
                </div>
                <div style={{ background: "#0d1117", borderRadius: 8, padding: "10px 14px", gridColumn: "span 2" }}>
                  <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 4 }}>Aggression Multiplier</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#a78bfa" }}>
                    {data.patterns.aggressivenessMultiplier.toFixed(2)}x
                    <span style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginLeft: 8 }}>
                      {data.patterns.aggressivenessMultiplier > 1.1 ? "Aggressive" : data.patterns.aggressivenessMultiplier < 0.9 ? "Conservative" : "Balanced"}
                    </span>
                  </div>
                </div>
              </div>
            </Section>

            {/* Raw Data Toggle */}
            <button
              onClick={() => setShowRaw(!showRaw)}
              style={{
                background: "#161b22",
                border: "1px solid #30363d",
                color: "#8b949e",
                borderRadius: 6,
                padding: "8px 16px",
                fontSize: 12,
                cursor: "pointer",
                marginBottom: 12
              }}
            >
              {showRaw ? "Hide" : "Show"} Raw Data
            </button>

            {showRaw && (
              <pre style={{
                background: "#161b22",
                border: "1px solid #30363d",
                borderRadius: 8,
                padding: 16,
                fontSize: 11,
                color: "#8b949e",
                overflow: "auto",
                maxHeight: 400
              }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            )}
          </>
        )}
      </div>
    </div>
  );
}
